import { createSelector } from 'reselect';
import get from 'lodash/get';
import has from 'lodash/has';
import filter from 'lodash/filter';
import minBy from 'lodash/minBy';
import sortBy from 'lodash/sortBy';
import reverse from 'lodash/reverse';
import queryStringBabel from 'query-string-babel';
import normalizeAd from '../normalize/ad';
import { relatedAdsSelector } from '../AdPage/selectors';
import { countAppliedFilters } from './utils';
import { getVerticalName } from '~/common/selectors/config';
import { getTrovitUrl } from '~/common/selectors/trovit';

const isFetchingState = state => state.serp.isFetching;
const errorState = state => state.serp.error;
const didYouMeanWhatState = state => state.serp.didYouMeanWhat;
const titleState = state => state.serp.title;
const descriptionState = state => state.serp.description;
const adsState = state => state.serp.ads;
const adsMetaState = state => state.serp.adsMeta;
const linksState = state => state.serp.links;
const facetsState = state => state.serp.facets;
const countState = state => state.serp.count;
const autoCompleteState = state => state.serp.autoComplete;
const locationState = state => state.serp.location;
const breadcrumbState = state => get(state, ['serp', 'interlinking', 'breadcrumb']);
const similarState = state => get(state, ['serp', 'interlinking', 'similar']);
const propertyTypeState = state => get(state, ['serp', 'interlinking', 'property_type']);
const geoState = state => get(state, ['serp', 'interlinking', 'geo']);
const geoHorizontalState = state => get(state, ['serp', 'interlinking', 'geo_horizontal']);
const makeState = state => get(state, ['serp', 'interlinking', 'make']);
const modelState = state => get(state, ['serp', 'interlinking', 'model']);
const selectedSeoState = state => get(state, ['serp', 'interlinking', 'selected_seo_links']);
const getOsmItems = state => get(state, ['serp', 'osm', 'items']);
const getAds = state => state.serp.ads;
const getTrovit = state => state.trovit;
const getConfig = state => state.config;
const getShouldRetrackAdImpressions = state => state.serp.shouldRetrackAdImpressions;
const getAdRedirectData = (state, ad, sectionId, sectionType, forceNoAdpage) => ({
  ad,
  sectionId,
  sectionType,
  forceNoAdpage,
});

export const shouldResetWhereSelector = state => state.serp.shouldResetWhere;

export const searchParametersState = state => state.serp.searchParameters;

export const searchParametersSelector = createSelector(
  searchParametersState,
  searchParameters => searchParameters
);

export const getFilters = state => get(searchParametersSelector(state), 'filters');
export const geoIdSelector = state => get(searchParametersSelector(state), 'geoId');
export const isUserFilterInteractionSelector = state =>
  get(searchParametersSelector(state), 'isUserFilterInteraction');
export const whatOriginalTypedSelector = state =>
  get(searchParametersSelector(state), 'whatOriginalTyped');
export const routeNameSelector = state => get(searchParametersSelector(state), 'routeName');
export const getPageName = state => get(searchParametersSelector(state), 'pageName');
export const getTypeId = state => get(searchParametersSelector(state), 'type_id');
export const getWhatAccented = state => get(searchParametersSelector(state), 'whatAccented');
export const getWhere = state => get(searchParametersSelector(state), 'where');
export const getSuggester = state => get(searchParametersSelector(state), 'suggester');
export const getFilterByName = (state, filterName) =>
  get(searchParametersSelector(state), ['filters', filterName]);
export const isUserSearchInteractionSelector = state =>
  get(searchParametersSelector(state), 'isUserSearchInteraction');
export const hasGeoId = state => !!geoIdSelector(state);
export const isSemUrlSelector = state => get(searchParametersSelector(state), 'isSemUrl');

export const isLandingRetentionSelector = state =>
  get(searchParametersSelector(state), 'boost_ads_since', false);

export const getSearchParameterWhatD = createSelector(
  searchParametersSelector,
  searchParameters => get(searchParameters, 'what_d')
);

export const getSearchParameterWhere = createSelector(
  searchParametersSelector,
  searchParameters => get(searchParameters, 'where')
);

export const getSearchParameterPage = createSelector(
  searchParametersSelector,
  searchParameters => get(searchParameters, 'page')
);

export const getIsSeoUrl = createSelector(
  searchParametersSelector,
  searchParameters => get(searchParameters, 'isSeoUrl')
);

export const filtersCountSelector = createSelector(
  getFilters,
  filters => countAppliedFilters(filters)
);

export const getSerpTitle = state => state.serp.title;

export function getOrganicAdsPerSection(state) {
  return (
    get(searchParametersSelector(state), ['sections', 'organic']) ||
    get(state.config, ['vertical', 'adsPerSections', 'organic'])
  );
}

export const getTypeName = (state, intl) => {
  if (!intl) {
    return '';
  }

  const typeId = getTypeId(state);
  const config = getConfig(state);
  const typeName = get(config, ['vertical', 'types', `${typeId}`]);
  return typeName && typeId !== 1 ? intl.formatMessage({ id: typeName }) : '';
};

export const getAdsVisitCounter = state => state.serp.adsVisitCounter;

export const isFetchingSelector = createSelector(
  isFetchingState,
  isFetching => isFetching
);
export const errorSelector = createSelector(
  errorState,
  error => error
);

export const shouldRetrackAdImpressionsSelector = createSelector(
  getShouldRetrackAdImpressions,
  shouldRetrackAdImpressions => shouldRetrackAdImpressions
);

export const didYouMeanWhatSelector = createSelector(
  didYouMeanWhatState,
  didYouMeanWhat => didYouMeanWhat
);

export const titleSelector = createSelector(
  titleState,
  title => title
);

export const descriptionSelector = createSelector(
  descriptionState,
  description => description
);

export const adsSelector = createSelector(
  adsState,
  ads => ads
);

export const adWithMinPriceSelector = createSelector(
  adsState,
  ads => {
    const filteredAds = filter(ads, ad => get(ad, ['attributes', 'vertical_properties', 'price']));
    const minByAds = minBy(filteredAds, ad =>
      get(ad, ['attributes', 'vertical_properties', 'price', 'value'])
    );

    return minByAds;
  }
);

export const newestAdsSelector = createSelector(
  adsState,
  ads => (ads.length > 0 ? reverse(sortBy(ads, ad => get(ad, ['attributes', 'date']))) : ads)
);

const adsIdsFilter = createSelector(
  adsState,
  ads => (ads.length > 1 && ads.map(ad => ad.id)) || null
);

export const adsIdsSelector = createSelector(
  adsIdsFilter,
  ads => ads
);

export const linksSelector = createSelector(
  linksState,
  links => links
);

export const facetsSelector = createSelector(
  facetsState,
  facets => facets
);

export const locationSelector = createSelector(
  locationState,
  location => location || null
);

export const countSelector = createSelector(
  countState,
  count => count
);

export const similarContentAdsCountSelector = state => state.serp.similarContentAdsCount;

export const autoCompleteSelector = createSelector(
  autoCompleteState,
  autoComplete => autoComplete
);

export const breadcrumbSelector = createSelector(
  breadcrumbState,
  breadcrumb => breadcrumb || null
);

export const similarSelector = createSelector(
  similarState,
  similar => similar || null
);

export const propertyTypeSelector = createSelector(
  propertyTypeState,
  propertyType => propertyType || null
);

export const geoSelector = createSelector(
  geoState,
  geo => geo || null
);

export const geoHorizontalSelector = createSelector(
  geoHorizontalState,
  geoHorizontal => geoHorizontal || null
);

export const makeSelector = createSelector(
  makeState,
  make => make || null
);

export const modelSelector = createSelector(
  modelState,
  model => model || null
);

export const getOldLocation = state => get(state, ['serp', 'interlinking', 'oldLocation']);

export const getOtherJobCategories = state =>
  get(state, ['serp', 'interlinking', 'otherJobCategories']);

export const selectedSeoSelector = createSelector(
  selectedSeoState,
  selectedSeo => selectedSeo || null
);

export const lastFreshAdSelector = createSelector(
  adsMetaState,
  adsMeta => (adsMeta.fresh ? get(adsMeta, ['fresh', 'current_page_count'], 0) : 0)
);

export const getOsmSelectedId = state => get(state, ['serp', 'osm', 'selectedId']);
export const getOsmIgnore = state => get(state, ['serp', 'osm', 'ignore']);

export const getIgnoreGeoId = state => get(state, ['serp', 'osm', 'ignoreGeoId']);

export const getCurrentOsmitem = createSelector(
  [getOsmItems, geoIdSelector],
  (osmItems, geoId) => {
    if (!geoId) {
      return null;
    }
    const osmItemsToShow = osmItems.filter(osmItem => osmItem.visible);

    return osmItemsToShow
      .reverse()
      .slice(0, 2)
      .map(osmItem => (osmItem ? osmItem.name : ''))
      .join(', ');
  }
);

export const getNormalizeAdsSelector = createSelector(
  [getAds, getVerticalName, getAdsVisitCounter],
  (ads, verticalName, adsVisitCounter) =>
    ads.map(ad => {
      const adVisitCounter = get(adsVisitCounter, ad.id);
      return normalizeAd(ad, verticalName, adVisitCounter ? get(adVisitCounter, 'count') : 0);
    })
);

export const isRelatedAdsPopupShow = state => state.serp.relatedAdsPopup.isShow;

export const getRelatedAdsPopupData = createSelector(
  [relatedAdsSelector],
  relatedAds => relatedAds.slice(0, 2)
);

export const getRelateAdsPopupRedirectUrl = state => state.serp.relatedAdsPopup.redirectUrl;

export const getRedirectData = createSelector(
  [getAdRedirectData, getTrovit, getConfig, searchParametersSelector],
  ({ ad, sectionId, sectionType, forceNoAdpage }, trovit, config, searchParameters) => {
    const origin = trovit.origin;
    const trackingCode = get(trovit, ['trackingCodes', `${origin}`]);
    const verticalName = config.verticalName;
    const abTest = trovit && trovit.abTest;

    return {
      country_code: config.countryCode,
      ad_id: ad.id,
      what: get(searchParameters, 'what_d'),
      type:
        verticalName === 'products'
          ? get(ad, ['verticalProperties', 'categoryId'])
          : get(searchParameters, 'type_id'),
      position: ad.position,
      page: get(searchParameters, 'page'),
      page_view_id: trovit.pageviewId,
      tracking_code: trackingCode,
      user_cookie_id: trovit.installationId,
      userCampaignIds: trovit.userCampaignIds,
      vertical_name: config.verticalName,
      origin_id: trovit.origin,
      is_force_no_adpage: forceNoAdpage === true,
      section_id: sectionId || ad.section_id,
      section_type: sectionType || ad.section_type_id,
      ab_test: abTest,
    };
  }
);

export const showingFiltersSelector = state => get(state, ['serp', 'showingFilters']);

export const getSearchUrl = createSelector(
  [getSearchParameterWhatD, getWhere, getTrovitUrl],
  (whatd, where, trovitUrl) => {
    const targetUrlSplitted = trovitUrl.split('q?');
    const targetUrlParsed = queryStringBabel.parse(targetUrlSplitted[1]);

    targetUrlParsed.what_d = whatd;
    targetUrlParsed.where = where;
    delete targetUrlParsed.suggester;

    return `${targetUrlSplitted[0]}q?${queryStringBabel.stringify(targetUrlParsed)}`;
  }
);

/* DISAMBIGUATION SELECTORS */

export const existsWhatParam = createSelector(
  [getSearchParameterWhatD, getFilters],
  (whatd, filters) => (typeof whatd === 'string' && whatd.length > 0) || has(filters, 'category')
);

export const existsGeolocationParam = createSelector(
  [searchParametersSelector],
  searchParameters => {
    const where = get(searchParameters, 'where');
    const filters = get(searchParameters, 'filters');

    return (
      (typeof where === 'string' && where.length > 0) ||
      has(filters, 'region') ||
      has(filters, 'city') ||
      has(filters, 'city_area')
    );
  }
);

export const getWhatLabel = createSelector(
  [searchParametersSelector],
  searchParameters => {
    const what = get(searchParameters, 'what_d');
    const filters = get(searchParameters, 'filters');

    return get(filters, 'category') || what || '';
  }
);

export const getWhereLabel = createSelector(
  [searchParametersSelector],
  searchParameters => {
    const where = get(searchParameters, 'where');
    const filters = get(searchParameters, 'filters');

    return (
      get(filters, 'city_area') || get(filters, 'city') || get(filters, 'region') || where || ''
    );
  }
);

export const getRedirectWhere = state => get(state, ['serp', 'redirectWhere']);

export const getRedirectWhat = state => get(state, ['serp', 'redirectWhat']);

export const isRetention = state => get(searchParametersSelector(state), 'boost_ads_since');



// WEBPACK FOOTER //
// ./app/Serp/selectors.js